// Supabase Auth bağlamı — oturum durumunu + şirket üyeliklerini uygulama
// geneline yayar.
//
// Üyelik kaynağı: public.company_members tablosu (RLS aktif, user JWT ile).
// app_metadata.company_id KULLANILMAZ — artık tek doğru kaynak company_members.

import * as React from "react";
import type { Session, User } from "@supabase/supabase-js";
import { createBrowserSupabase } from "./client";
import type { Membership } from "./types";

type Status = "loading" | "ready" | "not_configured";

type AuthState = {
  status: Status;
  session: Session | null;
  user: User | null;
  /** Üyelikler henüz yüklenmediyse null; yoksa []; varsa dizi. */
  memberships: Membership[] | null;
  /** Aktif şirket (şu an ilk üyelik; çoklu üyelik seçimi için ileride switcher). */
  companyId: string | null;
  /** Üyeliğin yüklenme hatası varsa mesajı; yoksa null. */
  membershipError: string | null;
  missing: string[];
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  signOut: () => Promise<void>;
};

const AuthCtx = React.createContext<AuthState | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const factory = React.useMemo(() => createBrowserSupabase(), []);
  const [session, setSession] = React.useState<Session | null>(null);
  const [status, setStatus] = React.useState<Status>(
    factory.ok ? "loading" : "not_configured",
  );
  const [memberships, setMemberships] =
    React.useState<Membership[] | null>(null);
  const [membershipError, setMembershipError] =
    React.useState<string | null>(null);
  const missing = !factory.ok ? factory.error.missing : [];
if (process.env.NODE_ENV !== "production") {
    console.debug("[IFM tanılama]", {
      configured: factory.ok,
      missing: factory.ok ? [] : factory.error.missing,
    });
  }
  // Oturum takibi: dinleyiciyi önce kur; getSession geç dönerse daha yeni
  // bir auth olayının sonucunu ezmesine izin verme.
  React.useEffect(() => {
    if (!factory.ok) return;

    const client = factory.client;
    let disposed = false;
    let authEventObserved = false;

    const { data: subscription } = client.auth.onAuthStateChange((_event, nextSession) => {
      authEventObserved = true;
      if (disposed) return;
      setSession(nextSession);
      setStatus("ready");
    });

    void client.auth
      .getSession()
      .then(({ data, error }) => {
        if (disposed || authEventObserved) return;
        if (error) {
          console.error("[Supabase] Oturum kontrolü:", error.message);
          setSession(null);
          return;
        }
        setSession(data.session);
      })
      .catch((error: unknown) => {
        if (disposed || authEventObserved) return;
        console.error("[Supabase] Oturum kontrolü başarısız:", error);
        setSession(null);
      })
      .finally(() => {
        if (!disposed) setStatus("ready");
      });

    return () => {
      disposed = true;
      subscription.subscription.unsubscribe();
    };
  }, [factory]);

  // Üyelik takibi — oturum değiştikçe company_members'tan yükle.
  // Önceki kullanıcıya ait geç tamamlanan sorgular yeni durumu ezemez.
  const userId = session?.user?.id ?? null;
  React.useEffect(() => {
    if (!factory.ok) return;

    let cancelled = false;
    if (!userId) {
      setMemberships(null);
      setMembershipError(null);
      return () => {
        cancelled = true;
      };
    }

    setMemberships(null);
    setMembershipError(null);

    const loadMemberships = async () => {
      try {
        // Supabase/network requests must not leave the whole app on an
        // infinite membership-loading screen. Surface a useful error if the
        // query never settles; this does not bypass auth or RLS.
        const membershipQuery = factory.client
          .from("company_members")
          .select("company_id, role")
          .eq("user_id", userId);
        const timeout = new Promise<never>((_, reject) => {
          setTimeout(
            () => reject(
              new Error(
                "Şirket üyeliği sorgusu 12 saniyede yanıt vermedi. Supabase bağlantısı ve company_members SELECT/RLS izinleri kontrol edilmeli.",
              ),
            ),
            12_000,
          );
        });
        const { data, error } = await Promise.race([membershipQuery, timeout]);

        if (cancelled) return;

        if (error) {
          console.error("[IFM üyelik hatası]", {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint,
          });
          setMembershipError(error.message);
          // [] durum rozetinin hatayı gösterebilmesini sağlar; hata mesajı
          // membershipError'da ayrıca tutulur ve boş üyelikle karıştırılmaz.
          setMemberships([]);
          return;
        }

        setMemberships((data as Membership[]) ?? []);
        setMembershipError(null);
      } catch (error: unknown) {
        if (cancelled) return;
        const message = error instanceof Error
          ? error.message
          : "Şirket üyelikleri yüklenirken beklenmeyen bir hata oluştu.";
        console.error("[IFM üyelik sorgusu başarısız]", error);
        setMembershipError(message);
        setMemberships([]);
      }
    };

    void loadMemberships();
    return () => {
      cancelled = true;
    };
  }, [factory, userId]);

  const signIn = React.useCallback<AuthState["signIn"]>(
    async (email, password) => {
      if (!factory.ok) {
        return {
          ok: false,
          message:
            "Supabase bağlantısı yapılandırılmadı. REACT_APP_SUPABASE_URL ve REACT_APP_SUPABASE_ANON_KEY değerleri henüz girilmedi.",
        };
      }
      const { error } = await factory.client.auth.signInWithPassword({
        email,
        password,
      });
      if (error) return { ok: false, message: error.message };
      return { ok: true };
    },
    [factory],
  );

  const signOut = React.useCallback(async () => {
    if (!factory.ok) return;
    await factory.client.auth.signOut();
    setMemberships(null);
    setMembershipError(null);
  }, [factory]);

  const value: AuthState = {
    status,
    session,
    user: session?.user ?? null,
    memberships,
    companyId: memberships && memberships.length > 0
      ? memberships[0].company_id
      : null,
    membershipError,
    missing,
    signIn,
    signOut,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export function useAuth(): AuthState {
  const ctx = React.useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
